import React, {useMemo} from "react";
import {Route} from "@/routing/Router";
import {generateBreadcrumbs} from "@widgets/layout/Breadcrumbs";
import {ActionRow, ActionRowButton, ActionRowMoreButton, Icon, PageHeading} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {ArrowDownTrayIcon, CalendarDaysIcon} from "@heroicons/react/24/outline";
import {DateRange} from "react-day-picker";
import {SetStateFn} from "@/utils/SetStateUtils";
import {useTooltip} from "@widgets/tooltip/TooltipProvider";
import {formatToShortDate} from "@/utils/DateUtils";
import LocationPageMeta from "@widgets/heading/meta/LocationPageMeta";

type Props = {
    route: Route,
    dateRange: DateRange,
    setShowDatePicker: SetStateFn<boolean>,
    setShowExportModal: SetStateFn<boolean>,
    dataLoading: boolean,
};

const TimesheetPageHeading = ({route, dateRange, setShowDatePicker, setShowExportModal, dataLoading}: Props) => {
    const intl = useIntl();
    const {applyTooltip} = useTooltip();
    const formattedRangeShort = useMemo(
        () => intl.formatMessage({id: "dateRangeShort"}, {
            from: formatToShortDate(dateRange.from),
            to: formatToShortDate(dateRange.to)
        }),
        [intl, dateRange]);

    return (
        <PageHeading
            breadcrumbs={generateBreadcrumbs(route, intl)}
            title={route.name(intl)}
            actionRow={
                <ActionRow
                    useMoreButton={true}
                    moreButton={
                        <ActionRowMoreButton>
                            <FormattedMessage id={"more"}/>
                        </ActionRowMoreButton>
                    }
                    moreButtonPositioning={"end"}
                    breakpoints={{
                        md: {
                            moreButtonPositioning: "start",
                        },
                    }}
                >
                    <ActionRowButton
                        color={"secondary"}
                        iconBefore={<Icon icon={ArrowDownTrayIcon}/>}
                        disabled={dataLoading}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: () => setShowExportModal(true),
                            }
                        }}
                    >
                        <FormattedMessage id={"export"}/>
                    </ActionRowButton>

                    <ActionRowButton
                        color={"primary"}
                        iconBefore={<Icon icon={CalendarDaysIcon}/>}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: () => setShowDatePicker(true),
                            },
                        }}
                        props={{
                            ...applyTooltip(intl.formatMessage({id: "pickARange"})),
                        }}
                        ariaProps={{
                            "aria-label": intl.formatMessage({id: "pickARange"}),
                        }}
                    >
                        {formattedRangeShort}
                    </ActionRowButton>
                </ActionRow>
            }
            meta={<LocationPageMeta/>}
            actionRowHorizontal={false}
            breakpoints={{"2xl": {actionRowHorizontal: true}}}
        />
    );
};

export default TimesheetPageHeading;